import { useEffect, useRef, useState } from 'react';
import { formatCustomerLabel } from '@/lib/customer-label';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPlus } from '@fortawesome/free-solid-svg-icons';
import {
  type CreateEquipmentPayload,
  type EquipmentStatus,
  type OwnershipType,
} from '@/services/equipment.service';
import { customerService } from '@/services/customer.service';
import { manufacturerService } from '@/services/manufacturer.service';
import { equipmentTypeService } from '@/services/equipment-type.service';
import { equipmentModelService } from '@/services/equipment-model.service';
import { tenantSettingsService } from '@/services/tenant-settings.service';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useHasPermission } from '@/hooks/use-permissions';
import { CustomerFormModal } from '@/features/customers/components/CustomerFormModal';
import {
  AddManufacturerModal,
  AddEquipmentTypeModal,
  AddEquipmentModelModal,
} from '@/features/master-data/components/MasterDataInlineModals';

const SELECT_CLASS =
  'flex h-10 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50';

// Native <option> popups do not inherit the select's `bg-card`: on Windows/
// Chromium the opened list paints on a white system background while the text
// keeps the light `--foreground`, rendering light-on-white and unreadable in
// dark mode. Setting an explicit background + text colour on each option forces
// a readable popup in both themes. Purely visual — no functional change.
const OPTION_CLASS = 'bg-card text-foreground';

export const STATUS_LABELS: Record<EquipmentStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  RETIRED: 'Retired',
};

const OWNERSHIP_LABELS: Record<OwnershipType, string> = {
  COMPANY: 'Company Inventory',
  CUSTOMER: 'Customer-Owned',
};

interface EquipmentFormProps {
  initial?: Partial<CreateEquipmentPayload>;
  onSubmit: (d: CreateEquipmentPayload) => void;
  onCancel: () => void;
  saving: boolean;
}

/** Reused verbatim from the original EquipmentPage — extracted so the Equipment
 * Intake Workspace's "Register Equipment" modal (Feature 5) can reuse the exact
 * same create form/validation without duplicating business logic. */
export function EquipmentForm({ initial, onSubmit, onCancel, saving }: EquipmentFormProps) {
  const [ownershipType, setOwnershipType] = useState<OwnershipType>(
    initial?.ownershipType ?? 'CUSTOMER',
  );
  const [customerId, setCustomerId] = useState(initial?.customerId ?? '');
  const [manufacturerId, setManufacturerId] = useState(initial?.manufacturerId ?? '');
  const [equipmentTypeId, setEquipmentTypeId] = useState(initial?.equipmentTypeId ?? '');
  const [equipmentModelId, setEquipmentModelId] = useState(initial?.equipmentModelId ?? '');
  const [equipmentName, setEquipmentName] = useState(initial?.equipmentName ?? '');
  const [assetNumber, setAssetNumber] = useState(initial?.assetNumber ?? '');
  const [serialNumber, setSerialNumber] = useState(initial?.serialNumber ?? '');
  const [purchaseDate, setPurchaseDate] = useState(
    initial?.purchaseDate ? initial.purchaseDate.slice(0, 10) : '',
  );
  const [installationDate, setInstallationDate] = useState(
    initial?.installationDate ? initial.installationDate.slice(0, 10) : '',
  );
  const [status, setStatus] = useState<EquipmentStatus>(initial?.status ?? 'ACTIVE');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);

  const qc = useQueryClient();
  const can = useHasPermission();

  const { data: customers } = useQuery({
    queryKey: ['customers-all'],
    queryFn: () => customerService.list({ pageSize: 100 }),
  });
  const { data: manufacturers } = useQuery({
    queryKey: ['manufacturers-all'],
    queryFn: () => manufacturerService.list({ pageSize: 100 }),
  });
  const { data: types } = useQuery({
    queryKey: ['equipment-types-all'],
    queryFn: () => equipmentTypeService.list({ pageSize: 100 }),
  });
  const { data: models } = useQuery({
    queryKey: ['equipment-models-all', manufacturerId, equipmentTypeId],
    queryFn: () =>
      equipmentModelService.list({
        pageSize: 100,
        manufacturerId: manufacturerId || undefined,
        equipmentTypeId: equipmentTypeId || undefined,
      }),
    enabled: !!manufacturerId,
  });
  const { data: numbering } = useQuery({
    queryKey: ['tenant-numbering-prefixes'],
    queryFn: tenantSettingsService.getNumbering,
  });

  // ── Inline master-data creation (Phase C.2 / C.3 / C.4) ────────────────────
  // Operations must never leave an in-progress intake to add a missing
  // manufacturer, equipment type or model. Each modal reuses the corresponding
  // Master Data form and renders as a page-level sibling below.
  const [manufacturerModalOpen, setManufacturerModalOpen] = useState(false);
  const [equipmentTypeModalOpen, setEquipmentTypeModalOpen] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);

  // A new manufacturer or type invalidates the current model selection, since
  // models are scoped to both.
  const handleManufacturerCreated = (created: { id: string }) => {
    setManufacturerId(created.id);
    setEquipmentModelId('');
  };

  const handleEquipmentTypeCreated = (created: { id: string }) => {
    setEquipmentTypeId(created.id);
    setEquipmentModelId('');
  };

  const handleModelCreated = (created: { id: string }) => {
    setEquipmentModelId(created.id);
  };

  // Pre-fill the Asset Number with the tenant's configured prefix for NEW
  // (manual) registrations only — never on edit, never overriding user input,
  // and only once. Asset numbers stay fully editable and are never auto-
  // sequenced (Issue 8).
  const assetPrefilledRef = useRef(false);
  useEffect(() => {
    if (assetPrefilledRef.current || !numbering) return;
    if (!initial?.assetNumber && numbering.assetNumberPrefix && assetNumber === '') {
      setAssetNumber(numbering.assetNumberPrefix);
    }
    assetPrefilledRef.current = true;
  }, [numbering, initial?.assetNumber, assetNumber]);

  const handleOwnershipChange = (type: OwnershipType) => {
    setOwnershipType(type);
    if (type === 'COMPANY') setCustomerId('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (ownershipType === 'CUSTOMER' && !customerId)
      errs.customerId = 'Customer is required for customer-owned equipment';
    if (!manufacturerId) errs.manufacturerId = 'Manufacturer is required';
    if (!equipmentTypeId) errs.equipmentTypeId = 'Equipment type is required';
    if (!equipmentModelId) errs.equipmentModelId = 'Equipment model is required';
    // Equipment name is optional (Issue 7) — no validation.
    if (!assetNumber.trim()) errs.assetNumber = 'Asset number is required';
    if (!serialNumber.trim()) errs.serialNumber = 'Serial number is required';
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    onSubmit({
      ownershipType,
      customerId: ownershipType === 'CUSTOMER' && customerId ? customerId : undefined,
      manufacturerId,
      equipmentTypeId,
      equipmentModelId,
      equipmentName: equipmentName.trim() || undefined,
      assetNumber,
      serialNumber,
      purchaseDate: purchaseDate ? new Date(purchaseDate).toISOString() : undefined,
      installationDate: installationDate ? new Date(installationDate).toISOString() : undefined,
      status,
      description: description || undefined,
      notes: notes || undefined,
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField label="Ownership Type" htmlFor="eq-ownership" required>
          <div className="flex gap-6 pt-1">
            {(['COMPANY', 'CUSTOMER'] as OwnershipType[]).map((type) => (
              <label key={type} className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="eq-ownership"
                  value={type}
                  checked={ownershipType === type}
                  onChange={() => handleOwnershipChange(type)}
                  disabled={saving}
                  className="h-4 w-4 accent-primary"
                />
                <span className="text-sm text-foreground">{OWNERSHIP_LABELS[type]}</span>
              </label>
            ))}
          </div>
        </FormField>

        {ownershipType === 'CUSTOMER' && (
          <FormField label="Customer" htmlFor="eq-customer" required error={errors.customerId}>
            <div className="flex items-center gap-2">
              <select
                id="eq-customer"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                disabled={saving}
                className={SELECT_CLASS}
              >
                <option value="" className={OPTION_CLASS}>
                  Select a customer...
                </option>
                {customers?.items.map((c) => (
                  <option key={c.id} value={c.id} className={OPTION_CLASS}>
                    {formatCustomerLabel(c)}
                  </option>
                ))}
              </select>
              {can('customer:create') && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setAddCustomerOpen(true)}
                  disabled={saving}
                  className="shrink-0 whitespace-nowrap"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1.5 h-3 w-3" /> Add Customer
                </Button>
              )}
            </div>
          </FormField>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Manufacturer" htmlFor="eq-mfr" required error={errors.manufacturerId}>
            <div className="flex items-center gap-2">
              <select
                id="eq-mfr"
                value={manufacturerId}
                onChange={(e) => {
                  setManufacturerId(e.target.value);
                  setEquipmentModelId('');
                }}
                disabled={saving}
                className={SELECT_CLASS}
              >
                <option value="" className={OPTION_CLASS}>
                  Select a manufacturer...
                </option>
                {manufacturers?.items.map((m) => (
                  <option key={m.id} value={m.id} className={OPTION_CLASS}>
                    {m.name}
                  </option>
                ))}
              </select>
              {can('manufacturer:create') && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setManufacturerModalOpen(true)}
                  disabled={saving}
                >
                  + Add
                </Button>
              )}
            </div>
          </FormField>

          <FormField
            label="Equipment Type"
            htmlFor="eq-type"
            required
            error={errors.equipmentTypeId}
          >
            <div className="flex items-center gap-2">
              <select
                id="eq-type"
                value={equipmentTypeId}
                onChange={(e) => {
                  setEquipmentTypeId(e.target.value);
                  setEquipmentModelId('');
                }}
                disabled={saving}
                className={SELECT_CLASS}
              >
                <option value="" className={OPTION_CLASS}>
                  Select a type...
                </option>
                {types?.items.map((t) => (
                  <option key={t.id} value={t.id} className={OPTION_CLASS}>
                    {t.name}
                  </option>
                ))}
              </select>
              {can('equipment-type:create') && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setEquipmentTypeModalOpen(true)}
                  disabled={saving}
                >
                  + Add
                </Button>
              )}
            </div>
          </FormField>
        </div>

        <FormField
          label="Equipment Model"
          htmlFor="eq-model"
          required
          error={errors.equipmentModelId}
        >
          <div className="flex items-center gap-2">
            <select
              id="eq-model"
              value={equipmentModelId}
              onChange={(e) => setEquipmentModelId(e.target.value)}
              disabled={!manufacturerId || saving}
              className={SELECT_CLASS}
            >
              <option value="" className={OPTION_CLASS}>
                {manufacturerId ? 'Select a model...' : 'Select a manufacturer first...'}
              </option>
              {models?.items.map((m) => (
                <option key={m.id} value={m.id} className={OPTION_CLASS}>
                  {m.name ? `${m.modelNumber} — ${m.name}` : m.modelNumber}
                </option>
              ))}
            </select>
            {can('model:create') && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setModelModalOpen(true)}
                disabled={saving || !manufacturerId || !equipmentTypeId}
                title={
                  !manufacturerId || !equipmentTypeId
                    ? 'Select a manufacturer and equipment type first'
                    : undefined
                }
              >
                + Add
              </Button>
            )}
          </div>
        </FormField>

        <FormField label="Equipment Name" htmlFor="eq-name" error={errors.equipmentName}>
          <Input
            id="eq-name"
            value={equipmentName}
            onChange={(e) => setEquipmentName(e.target.value)}
            placeholder="Optional — e.g. Main Chiller Unit"
            disabled={saving}
          />
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Asset Number" htmlFor="eq-asset" required error={errors.assetNumber}>
            <Input
              id="eq-asset"
              value={assetNumber}
              onChange={(e) => setAssetNumber(e.target.value)}
              placeholder="AST-0001"
              disabled={saving}
            />
          </FormField>
          <FormField label="Serial Number" htmlFor="eq-serial" required error={errors.serialNumber}>
            <Input
              id="eq-serial"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="SN-123456"
              disabled={saving}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Purchase Date" htmlFor="eq-purchase">
            <Input
              id="eq-purchase"
              type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              disabled={saving}
            />
          </FormField>
          <FormField label="Installation Date" htmlFor="eq-install">
            <Input
              id="eq-install"
              type="date"
              value={installationDate}
              onChange={(e) => setInstallationDate(e.target.value)}
              disabled={saving}
            />
          </FormField>
        </div>

        <FormField label="Status" htmlFor="eq-status">
          <select
            id="eq-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as EquipmentStatus)}
            disabled={saving}
            className={SELECT_CLASS}
          >
            {(Object.keys(STATUS_LABELS) as EquipmentStatus[]).map((s) => (
              <option key={s} value={s} className={OPTION_CLASS}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Description" htmlFor="eq-desc">
          <Textarea
            id="eq-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            disabled={saving}
          />
        </FormField>

        <FormField label="Notes" htmlFor="eq-notes">
          <Textarea
            id="eq-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            disabled={saving}
          />
        </FormField>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving} className="min-w-[100px]">
            {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Save'}
          </Button>
        </div>
      </form>

      {/* Quick "Add Customer" without leaving the Register Equipment dialog.
       * The equipment form state lives in this component, so opening the nested
       * modal does not unmount it — entered equipment data is preserved. On
       * create we refresh the customer list and auto-select the new customer;
       * the modal stays open on its one-time-credentials step until dismissed. */}
      <CustomerFormModal
        open={addCustomerOpen}
        editTarget={null}
        onClose={() => setAddCustomerOpen(false)}
        onSaved={() => void qc.invalidateQueries({ queryKey: ['customers-all'] })}
        onCreated={(c) => {
          void qc.invalidateQueries({ queryKey: ['customers-all'] });
          setOwnershipType('CUSTOMER');
          setCustomerId(c.id);
          setErrors((prev) => ({ ...prev, customerId: '' }));
        }}
      />

      {/* Inline master-data creation (C.2 / C.3 / C.4). Page-level siblings, so
       * the shared Modal stack owns Escape targeting and the scroll lock. Each
       * modal invalidates its own query and reports the created record, which
       * the handlers above auto-select. */}
      <AddManufacturerModal
        open={manufacturerModalOpen}
        onClose={() => setManufacturerModalOpen(false)}
        onCreated={handleManufacturerCreated}
      />
      <AddEquipmentTypeModal
        open={equipmentTypeModalOpen}
        onClose={() => setEquipmentTypeModalOpen(false)}
        onCreated={handleEquipmentTypeCreated}
      />
      <AddEquipmentModelModal
        open={modelModalOpen}
        onClose={() => setModelModalOpen(false)}
        manufacturerId={manufacturerId}
        equipmentTypeId={equipmentTypeId}
        onCreated={handleModelCreated}
      />
    </>
  );
}
